import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { ixApi } from "./api";
import { toast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const useGetAvailableFlows = (connectionId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.availableFlows(connectionId),
    () => ixApi.getAvailableFlows(connectionId),
    {
      enabled: !!connectionId,
    },
  );
};

export const useCreateConnectionFlow = (connectionId: string) => {
  return useSmartMutation(
    (payload: any) => ixApi.createConnectionFlow(connectionId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Sync flow created successfully.",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ix", "availableFlows", connectionId],
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ix.connectionFlows(connectionId),
        });
      },
      onError: (err: Error) => {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useGetConnectionFlows = (connectionId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.connectionFlows(connectionId),
    () => ixApi.getConnectionFlows(connectionId),
    {
      enabled: !!connectionId,
    },
  );
};

export const useUpdateConnectionFlow = (connectionId: string) => {
  return useSmartMutation(
    ({
      flowId,
      payload,
    }: {
      flowId: string;
      payload: { is_active: boolean };
    }) => ixApi.updateConnectionFlow(connectionId, flowId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Sync flow status updated successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ix.connectionFlows(connectionId),
        });
      },
      onError: (err: Error) => {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useDeleteConnectionFlow = (connectionId: string) => {
  return useSmartMutation(
    (flowId: string) => ixApi.deleteConnectionFlow(connectionId, flowId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Sync flow deleted successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ix.connectionFlows(connectionId),
        });
        queryClient.refetchQueries({
          queryKey: ["ix", "editconnectionDetails", connectionId],
        });
        queryClient.refetchQueries({
          queryKey: ["ix", "connectionDetails", connectionId],
        });
        queryClient.refetchQueries({
          queryKey: ["ix", "availableFlows", connectionId],
        });
      },
      onError: (err: Error) => {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useActivateConnection = (connectionId: string) => {
  return useSmartMutation(() => ixApi.activateConnection(connectionId), {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Integration activated successfully.",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: ["ix", "erp-integration"],
      });
      queryClient.refetchQueries({
        queryKey: ["ix", "editconnectionDetails", connectionId],
      });
      queryClient.refetchQueries({
        queryKey: ["ix", "connectionDetails", connectionId],
      });
      queryClient.refetchQueries({
        queryKey: ["ix", "connectorsCatalog"],
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });
};

export const useGetConnectionHealthTimeline = (connectionId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.healthTimeline(connectionId),
    () => ixApi.getConnectionHealthTimeline(connectionId),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetConnectionStats = (
  connectionId: string,
  period: string = "24h",
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.connectionStats(connectionId, period),
    () => ixApi.getConnectionStats(connectionId, period),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetConnectionFlowsSummary = (connectionId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.flowsSummary(connectionId),
    () => ixApi.getConnectionFlowsSummary(connectionId),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetHealthTimeline = (connectionId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.healthTimeline(connectionId),
    () => ixApi.getHealthTimeline(connectionId),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetSyncLogs = (connectionId: string, filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.syncLogs(connectionId, filters),
    () => ixApi.getSyncLogs(connectionId, filters),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetConnectionErrors = (connectionId: string, filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.connectionErrors(connectionId, filters),
    () => ixApi.getConnectionErrors(connectionId, filters),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetMonitoring = (connectionId: string, period: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.monitoring(connectionId, period),
    () => ixApi.getMonitoring(connectionId, period),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetVersions = (connectionId: string, filters: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.ix.versions(connectionId, filters),
    () => ixApi.getVersions(connectionId, filters),
    {
      enabled: !!connectionId,
    },
  );
};

export const useGetConnectionDetails = (connectionId: string) => {
  const remote_utility_id = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ix.connectionDetails(connectionId),
    () => ixApi.getConnectionDetails(connectionId, remote_utility_id),
    {
      enabled: !!connectionId && !!remote_utility_id,
    },
  );
};
